/**
 * The event contract.
 *
 * Shared by the browser beacon and the ingest Worker so the two cannot drift.
 * The Worker validates every incoming event against this list -- the ingest
 * endpoint is public and unauthenticated, so anything not declared here is
 * rejected rather than written.
 *
 * ## Rules for adding an event
 *
 * - Never record anything that identifies a person. The device id is a random
 *   opaque string with no email, no name, no IP. Country comes from the
 *   Cloudflare edge and is stored as a two-letter code.
 * - Numeric properties belong in `doubles`, strings in `blobs`. Analytics
 *   Engine caps each at 20 per data point, which is far more than any event
 *   here needs.
 * - Events are cheap; a missing event costs you a week of launch data you can
 *   never get back. Err towards recording.
 */

export const SCHEMA_VERSION = 1;

/** Every event the client may send. Anything else is dropped at ingest. */
export const EVENTS = {
  // --- session -------------------------------------------------------------
  first_open: ['referrer'],
  session_start: [],
  session_end: ['durationMs', 'screens'],
  mode_selected: ['mode'],

  // --- draft funnel --------------------------------------------------------
  // The most valuable group. Drop-off by pick number tells you exactly where
  // the draft loses people, which is the single most actionable chart you have.
  year_range_confirmed: ['fromYear', 'toYear'],
  spin: ['pickNumber', 'squadLabel'],
  player_picked: ['pickNumber', 'slot', 'rating', 'isCapWinner', 'isOverseas', 'role'],
  pick_blocked: ['reason'],
  respin_used: ['source', 'tokensLeft'],
  draft_abandoned: ['pickNumber', 'elapsedMs'],
  draft_completed: ['elapsedMs', 'overseas', 'bowlers', 'capWinners', 'respinsUsed'],
  team_named: [],

  // --- match ---------------------------------------------------------------
  toss_result: ['wonToss', 'elected'],
  match_start: ['mode', 'opponentType'],
  bowler_selected: ['over', 'bowlerRating'],
  impact_sub_used: ['over'],
  timeout_called: ['side', 'over', 'forced', 'aggression'],
  match_completed: ['result', 'margin', 'scoreFor', 'scoreAgainst', 'durationMs', 'mode'],
  match_abandoned: ['over', 'mode'],
  super_over: ['result'],

  // --- retention -----------------------------------------------------------
  daily_challenge_played: ['margin', 'rank'],
  streak_extended: ['length'],
  streak_broken: ['length'],
  return_visit: ['daysSinceFirst'],
  badge_earned: ['badge'],

  // --- 1v1 -----------------------------------------------------------------
  room_created: [],
  invite_shared: ['channel'],
  room_joined: [],
  room_expired: [],
  room_extended: [],
  match_resolved: ['durationMs'],
  opponent_timeout: [],

  // --- ads -----------------------------------------------------------------
  ad_requested: ['format', 'placement'],
  ad_shown: ['format', 'placement'],
  ad_completed: ['format', 'placement'],
  ad_dismissed: ['format', 'placement'],
  ad_failed: ['format', 'placement', 'reason'],

  // --- technical -----------------------------------------------------------
  load_time: ['ttiMs', 'cacheHit'],
  js_error: ['message', 'source'],
  engine_error: ['message'],
  /**
   * Both an error metric and the cheat-detection signal: a submitted result
   * that does not reproduce when the Worker replays its seed.
   */
  replay_mismatch: ['mode'],
  api_latency: ['route', 'ms'],
} as const;

export type EventName = keyof typeof EVENTS;

export const EVENT_NAMES = Object.keys(EVENTS) as EventName[];
const EVENT_SET = new Set<string>(EVENT_NAMES);

export const isKnownEvent = (name: string): name is EventName => EVENT_SET.has(name);

/** Context attached to every event in a batch, sent once rather than per event. */
export interface BatchContext {
  /** Random opaque device id. Not an account, not derived from anything personal. */
  d: string;
  /** Session id, regenerated after 30 minutes idle. */
  s: string;
  /** App version. */
  a: string;
  /** Coefficient set version the client is running. */
  c: string;
  /** Device class: 'mobile' | 'tablet' | 'desktop'. */
  dc: string;
  /** Connection type from the Network Information API, or 'unknown'. */
  ct: string;
}

export interface WireEvent {
  /** Event name. */
  n: string;
  /** Milliseconds since the batch's first event. */
  t: number;
  /** Properties. Strings and numbers only. */
  p?: Record<string, string | number | boolean>;
}

export interface Batch extends BatchContext {
  v: number;
  e: WireEvent[];
}

// ---------------------------------------------------------------------------
// Limits
// ---------------------------------------------------------------------------

/**
 * The ingest endpoint is public and unauthenticated, so every one of these is
 * a hard cap enforced server-side. A generous limit is still a limit.
 */
export const LIMITS = {
  maxBodyBytes: 16 * 1024,
  maxEventsPerBatch: 64,
  maxPropsPerEvent: 12,
  maxStringLength: 200,
  maxIdLength: 64,
} as const;

/** Property names whose presence would mean we are collecting something we should not. */
const FORBIDDEN_PROPS = new Set(['email', 'name', 'ip', 'phone', 'address', 'password', 'token']);

export interface ValidationResult {
  readonly ok: boolean;
  readonly reason?: string;
}

/** Shallow structural validation. Returns the first problem found. */
export function validateBatch(body: unknown): ValidationResult {
  if (typeof body !== 'object' || body === null) return { ok: false, reason: 'not an object' };
  const b = body as Partial<Batch>;

  if (b.v !== SCHEMA_VERSION) return { ok: false, reason: `unsupported schema version ${String(b.v)}` };

  for (const key of ['d', 's', 'a', 'c', 'dc', 'ct'] as const) {
    const value = b[key];
    if (typeof value !== 'string' || value.length === 0) return { ok: false, reason: `missing ${key}` };
    if (value.length > LIMITS.maxIdLength) return { ok: false, reason: `${key} too long` };
  }

  if (!Array.isArray(b.e)) return { ok: false, reason: 'events must be an array' };
  if (b.e.length === 0) return { ok: false, reason: 'empty batch' };
  if (b.e.length > LIMITS.maxEventsPerBatch) return { ok: false, reason: 'too many events' };

  for (const event of b.e) {
    if (typeof event !== 'object' || event === null) return { ok: false, reason: 'malformed event' };
    if (typeof event.n !== 'string' || !isKnownEvent(event.n)) {
      return { ok: false, reason: `unknown event ${String(event.n)}` };
    }
    if (typeof event.t !== 'number' || !Number.isFinite(event.t) || event.t < 0) {
      return { ok: false, reason: `bad timestamp on ${event.n}` };
    }
    if (event.p !== undefined) {
      if (typeof event.p !== 'object' || event.p === null) return { ok: false, reason: 'bad props' };
      const keys = Object.keys(event.p);
      if (keys.length > LIMITS.maxPropsPerEvent) return { ok: false, reason: `too many props on ${event.n}` };
      for (const key of keys) {
        if (FORBIDDEN_PROPS.has(key.toLowerCase())) {
          return { ok: false, reason: `property "${key}" must never be collected` };
        }
        const value = event.p[key];
        const t = typeof value;
        if (t !== 'string' && t !== 'number' && t !== 'boolean') {
          return { ok: false, reason: `property "${key}" must be a string, number or boolean` };
        }
        if (t === 'string' && (value as string).length > LIMITS.maxStringLength) {
          return { ok: false, reason: `property "${key}" too long` };
        }
        if (t === 'number' && !Number.isFinite(value as number)) {
          return { ok: false, reason: `property "${key}" is not finite` };
        }
      }
    }
  }

  return { ok: true };
}
